import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Flex, Grid } from 'components/box';
import {
  Cart,
  CustomizableOptionValueInterface,
  Money,
  ProductInterface,
  ProductTypeEnum,
} from 'types/types';
import { Heading, Text } from 'components/typography';
import { NotReturnableModal } from 'components/_pages/products/modals';
// TODO: swap back to the version that checks the flag once returns are used correctly
import ReturnableModal from 'components/checkout/modals/returns';
import { minQuantity } from '../../../../util/products';
import { isCustomizable, isPhysical } from 'types/guards/product';
import { useAddItemToCart } from 'hooks/cart';
import {
  Gift as GiftIcon,
  CornerUpLeft as ReturnIcon,
  Star as StarIcon,
} from 'react-feather';
import SharingActions from 'components/_pages/products/sharing-actions';
import { css } from '@emotion/react';
import dynamic from 'next/dynamic';
import resourceUrl from '../../../../util/make-url';
import {
  generateVariantIdFromSelectedOption,
  mapCartItemToPayload,
  mapProductToPayload,
  trackCartEvent,
} from '../../../../util/tag-manager';
import { useAddedToCartNotification, useCustomOptions } from 'hooks/product';
import { useNavigationBarActive } from 'hooks/app';
import {
  NAVIGATION_BAR_HEIGHT_MOBILE,
  NAVIGATION_BAR_HEIGHT_TABLET,
} from 'components/_global/navigation-bar';
import config from '../../../../util/load-config';
import AddedToCartNotificationBody from 'components/_shared/widgets/product/added-to-cart-notification-body';
import QuantitySelector from 'components/_shared/widgets/product/quantity-selector';
import AddToCartButton from 'components/_shared/widgets/product/add-to-cart-button';
import { actionToStepMap, EventNamesEnum } from 'types/third-party/gtm';
import { useCustomer } from 'hooks/customer';
import InfoLabel from 'components/_pages/products/buttons/info-label';
import { useRouterPush } from 'components/configurable-routing';
import { usePostInsights } from 'hooks/algolia';
import { useSearchParams } from 'next/navigation';

const CustomOptionSelector = dynamic(
  () => import('components/_shared/widgets/product/custom-option-selector')
);

const Actions = ({
  openGiftModal,
  product,
  displayPrice,
  canShowProduct,
  setSelectedOption,
  isStaffMember,
  hidePriceRange,
  openSizeGuide,
}: {
  openGiftModal: () => void;
  product: ProductInterface;
  displayPrice: Money;
  canShowProduct: boolean;
  setSelectedOption: (
    variant: CustomizableOptionValueInterface | undefined
  ) => void;
  isStaffMember: boolean;
  hidePriceRange?: boolean;
  openSizeGuide?: () => void;
}) => {
  const {
    areCustomOptionsValid,
    addToCartOptions,
    selectedOption,
    setCustomOptionsState,
  } = useCustomOptions();

  const user = useCustomer();

  const push = useRouterPush();

  const postInsights = usePostInsights();

  const params = useSearchParams();

  const actionsRef = useRef<HTMLDivElement>(null);
  const isNavigationBarActive = useNavigationBarActive();
  const addedToCartNotification = useAddedToCartNotification();

  const [quantity, setQuantity] = useState(minQuantity(product.stockConfig));

  const xLeftQuantity =
    selectedOption?.xLeftQuantity || product.xLeftQuantity || null;

  const [isNotReturnableOpen, setIsNotReturnableOpen] = useState(false);
  const [highlightEmptyOptions, setHighlightEmptyOptions] = useState(false);
  const [isProductReturnableOpen, setIsProductReturnableOpen] = useState(false);

  const isProductReturnable = !product.isUnderwearOrSwimwear;

  const canEnableActions = !product.isSoldOut || isStaffMember;

  const notificationImageUrl = resourceUrl(
    selectedOption?.image?.url || product.image.url,
    { width: 40 }
  );

  const onAddToCartComplete = useCallback(
    ({ cart, message }: { cart: Cart; message?: string }) => {
      const { items, totals, voucher } = cart;
      const vouchers = totals?.discount?.items;

      const trackEvent = () =>
        trackCartEvent({
          cartItems: items.map(mapCartItemToPayload),
          event: EventNamesEnum.beginCheckout,
          stepNumber: actionToStepMap[EventNamesEnum.beginCheckout],
          callback: () => push({ url: '/checkout' }),
          amounts: {
            amount: totals?.grandTotal.value,
            subTotal: totals?.subtotal.value,
            totalProductQuantity: items.reduce(
              (acc, item) => (acc += item.quantity),
              0
            ),
            voucherCode: voucher,
            voucherDiscount: (vouchers || []).reduce(
              (acc, voucher) => acc + voucher.amount.value,
              0
            ),
          },
        });

      addedToCartNotification({
        trackEventCallback: trackEvent,
        product,
        body: (
          <AddedToCartNotificationBody
            imageUrl={notificationImageUrl}
            quantity={quantity}
            price={displayPrice}
            message={message}
          />
        ),
      });

      trackCartEvent({
        product: mapProductToPayload({
          product,
          variantId: generateVariantIdFromSelectedOption(
            product.realId,
            selectedOption
          ),
        }),
        event: EventNamesEnum.addToCart,
        productQuantity: quantity,
        user,
      });
    },
    [
      addedToCartNotification,
      displayPrice,
      notificationImageUrl,
      product,
      push,
      quantity,
      selectedOption,
      user,
    ]
  );

  const [addToCart, { loading }] = useAddItemToCart({
    onComplete: onAddToCartComplete,
  });

  const canAddToCart = useCallback(() => {
    if (!canShowProduct || loading) {
      return false;
    }

    if (isCustomizable(product)) {
      return areCustomOptionsValid;
    }

    return true;
  }, [canShowProduct, product, loading, areCustomOptionsValid]);

  const isNotGiftvoucher = product.permanentShop?.id !== 'gift-vouchers';

  const highlightOptions = useCallback(() => {
    setHighlightEmptyOptions(true);

    const offset =
      (actionsRef.current?.offsetTop && actionsRef.current.offsetTop - 100) ||
      999999;

    if (window && window.scrollY > offset) {
      window.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    }
  }, [setHighlightEmptyOptions]);

  const addProductToCart = async () => {
    setIsNotReturnableOpen(false);

    await addToCart({
      type: ProductTypeEnum.Simple,
      sku: product.sku,
      quantity,
      customOptions: addToCartOptions,
    });

    const queryId = params.get(config.algolia.paramQueryIdName);
    const indexName = params.get(config.algolia.paramIndexName);

    if (typeof queryId === 'string' && typeof indexName === 'string') {
      postInsights({
        queryID: queryId,
        objectID: `${product.realId}`,
        index: indexName,
        eventType: 'conversion',
        eventName: 'Product added to cart after search',
      });
    }
  };

  const addToCartClick = () => {
    if (!canAddToCart()) {
      highlightOptions();
      return;
    }

    if (product.isReturnable && !product.isUnderwearOrSwimwear) {
      addProductToCart().then();
    } else {
      setIsNotReturnableOpen(true);
    }
  };

  /**
   * Update variant based on selected option.
   */
  useEffect(() => {
    setSelectedOption(selectedOption);
  }, [selectedOption, setSelectedOption]);

  const { hasPriceRange } = product;
  const hideGiftOption =
    typeof product.isGiftable === 'boolean' && !product.isGiftable;

  const price = displayPrice || product.price;

  const PRODUCT_ACTIONS_WIDTH_DESKTOP = '60%';

  return (
    <>
      <Box gridArea="actions">
        <Grid
          ref={actionsRef}
          /**
           * After discussions with other devs, I have decided to keep this
           * code snippet commented in case business change their mind.
           *
           * The new code changes the design to simply stack each selector in a column
           * as part of a PDP redesign, rather than having them side by side
           * in descending rows.
           */
          // gridTemplateColumns={['1fr', 'repeat(2, minmax(0, 1fr))']}
          gridTemplateColumns={['1fr', PRODUCT_ACTIONS_WIDTH_DESKTOP]}
          gridRowGap="1.25em"
          gridColumnGap="2.2em"
          pb={[0, null, 3]}
          mb={[1, 0]}
          position="relative"
        >
          {isCustomizable(product) && (
            <CustomOptionSelector
              openSizeGuide={openSizeGuide}
              product={product}
              setCustomOptionsState={setCustomOptionsState}
              highlightEmptyOptions={highlightEmptyOptions}
              disabled={!canEnableActions}
            />
          )}

          {canShowProduct &&
            !product.externalListingLink &&
            isNotGiftvoucher && (
              <QuantitySelector
                quantity={quantity}
                setQuantity={setQuantity}
                xLeftQuantity={xLeftQuantity}
                stockConfig={product.stockConfig}
                disabled={!canEnableActions}
              />
            )}
        </Grid>
      </Box>

      {canShowProduct && (
        <Grid
          gridArea="button"
          gridTemplateColumns={['1fr 1fr', null, '1fr']}
          gridColumnGap={0}
          alignItems="center"
          backgroundColor="white"
          position={['sticky', null, 'unset']}
          bottom={
            isNavigationBarActive
              ? [
                  `${NAVIGATION_BAR_HEIGHT_MOBILE + 16}px`,
                  `${NAVIGATION_BAR_HEIGHT_TABLET + 16}px`,
                  'unset',
                ]
              : [3, null, 'unset']
          }
          boxShadow={['0px 0px 6px 1px rgba(0, 0, 0, 0.2)', null, 'unset']}
          borderRadius={[2, null, 'unset']}
          zIndex={[190, null, 'unset']}
          p={[2, null, 0]}
          m={0}
          mt={[3, 2, 0]}
          css={theme => css`
            @media ${theme.mediaQueries.tinyScreenOnly} {
              grid-template-columns: 1fr 2fr;
            }
          `}
        >
          <Box className="hide-for-desktop">
            <Flex
              justifyContent="flex-start"
              alignItems="flex-start"
              flexDirection="column"
              ml={[2, 0]}
              css={theme => css`
                @media ${theme.mediaQueries.tinyScreenOnly} {
                  flex-direction: column;
                  align-items: flex-start;
                }
              `}
            >
              <Heading fontSize={[3, 5]} mb={hidePriceRange ? 0 : 1}>
                {hasPriceRange && !hidePriceRange
                  ? product.minPrice.formattedValue
                  : price.formattedValue}
              </Heading>
              {(!hidePriceRange || !hasPriceRange) &&
                product.retailPrice.value > 0 && (
                  <Text
                    color="darkGrey"
                    fontFamily="header"
                    fontWeight="500"
                    fontSize={[2, 3]}
                    css={css`
                      text-decoration: line-through;
                    `}
                  >
                    {product.retailPrice.formattedValue}
                  </Text>
                )}
            </Flex>
          </Box>

          <Box width={['100%', null, PRODUCT_ACTIONS_WIDTH_DESKTOP]}>
            <AddToCartButton
              product={product}
              callback={addToCartClick}
              quantity={quantity}
              loading={loading}
            />
          </Box>
        </Grid>
      )}

      {canShowProduct && (
        <Flex
          flexDirection="column"
          justifyContent="space-between"
          gridArea="additional"
        >
          {!product.externalListingLink && (
            <Flex
              justifyContent={[
                'flex-start',
                null,
                isPhysical(product) && !!product.sizeChartImage?.url
                  ? 'space-between'
                  : 'flex-start',
              ]}
              alignItems="middle"
              mb={3}
              mt={[3, 0]}
              flexDirection={['column-reverse', 'row', 'column']}
              css={theme => css`
                gap: ${theme.space[3]}px;
              `}
            >
              <InfoLabel
                onClick={() => setIsProductReturnableOpen(true)}
                icon={ReturnIcon}
                text={
                  isProductReturnable
                    ? 'Easy returns within 7 days'
                    : "This product can't be returned"
                }
              />

              {isNotGiftvoucher && !hideGiftOption && (
                <InfoLabel
                  onClick={openGiftModal}
                  icon={GiftIcon}
                  text="Send as a gift to friends & family"
                />
              )}

              <InfoLabel
                text="Customers rate OneDayOnly 4.5/5 on Google"
                icon={StarIcon}
              />
            </Flex>
          )}

          <Box className="hide-for-mobile-only">
            <SharingActions product={product} />
          </Box>
        </Flex>
      )}

      {canShowProduct && (
        <>
          <ReturnableModal
            close={() => setIsProductReturnableOpen(false)}
            isOpen={isProductReturnableOpen}
            isReturnable={isProductReturnable}
          />
          <NotReturnableModal
            close={() => setIsNotReturnableOpen(false)}
            confirm={addProductToCart}
            isOpen={isNotReturnableOpen}
            isReturnable={
              product.isReturnable && !product.isUnderwearOrSwimwear
            }
          />
        </>
      )}
    </>
  );
};

export default Actions;
